import React from 'react';
import type { AddressType } from '@sweetgreen/garnish';
import {
  AddressTypes,
  Button,
  IllusDelivery_1,
  IllusOutpost_1,
  IllusPickup_1,
  switchcase,
  useBreakpointHelpers,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { FeaturedChannel } from '../FeaturedChannels.components';

export const PrimaryChannel = (props: PrimaryChannelProps) => {
  const { checkMinWidth } = useBreakpointHelpers();
  const { labelCopy, buttonCopy } = usePrimaryCopy(props.orderType);
  const illustration = usePrimaryIllus(props.orderType);

  return (
    <FeaturedChannel testID="featured-channel.primary.card">
      <FeaturedChannel.Illustration source={illustration} />
      <FeaturedChannel.TextContainer>
        <FeaturedChannel.Label>{labelCopy}</FeaturedChannel.Label>
        <FeaturedChannel.Name>{props.name}</FeaturedChannel.Name>
      </FeaturedChannel.TextContainer>
      <FeaturedChannel.ButtonWrapper>
        <Button
          testID="featured-channel.primary.button"
          onPress={props.onOrder}
          isLoading={props.isLoading}
          size={checkMinWidth.desktop ? 'large' : 'medium'}
        >
          {buttonCopy}
        </Button>
      </FeaturedChannel.ButtonWrapper>
    </FeaturedChannel>
  );
};

// ────────────────── Hooks ──────────────────

const usePrimaryCopy = (orderType: AddressType) => {
  const { t } = useLocalizationContext();

  return {
    labelCopy: t('featured-channel.primary.label'),
    buttonCopy: t('featured-channel.primary.button', { orderType }),
  };
};

const usePrimaryIllus = (orderType: AddressType) => {
  return switchcase({
    [AddressTypes.pickup]: IllusPickup_1,
    [AddressTypes.outpost]: IllusDelivery_1,
    [AddressTypes.delivery]: IllusOutpost_1,
  })(IllusPickup_1)(orderType);
};

// ────────────────── Types ──────────────────

type PrimaryChannelProps = {
  readonly orderType: AddressType;
  readonly name: string;
  readonly isLoading: boolean;
  readonly onOrder: () => void;
};
